/**
 * Structured documentation content for the in-app Documentation pages.
 * Each section has an id, title, audience tag, and content blocks.
 *
 * audience: "admin" | "teacher" | "student" | "parent"
 *
 * Block types: paragraph | subheading | note | list | table | faq
 */

export const DOC_SECTIONS = [
  // ─── ADMIN ──────────────────────────────────────────────────────────────────
  {
    id: "first-login",
    title: "First Login & Setup Wizard",
    audience: ["admin"],
    content: [
      {
        type: "paragraph",
        text: "When you log in as an administrator for the first time, you are guided through a short setup wizard before the main panel.",
      },
      {
        type: "list",
        items: [
          "School Profile — name, tagline, and contact email",
          "Teacher Account — create the first teacher (or confirm your own)",
          "First Student — optionally add your first student",
          "Welcome — completion confirmation",
        ],
      },
      {
        type: "note",
        text: "Teachers bypass the wizard and go straight to the dashboard. You can re-run it from Settings → Admin Setup.",
      },
    ],
  },
  {
    id: "dashboard",
    title: "Dashboard",
    audience: ["admin", "teacher"],
    content: [
      {
        type: "paragraph",
        text: "The dashboard is your daily command centre. It combines operational stats with the sales pipeline.",
      },
      {
        type: "list",
        items: [
          "Student counts — total, pending, and approved",
          "Today's classes — sessions scheduled for today, with the teacher and student count",
          "Upcoming classes — scheduled in the next 7 days",
          "Hot leads — students who requested a consultant (need follow-up)",
          "Cold leads — students who took the level test but went quiet for 48h+",
          "Expiring enrollments — active plans ending within 7 days",
          "Revenue — completed payments for the last 6 months (shown in the configured currency)",
        ],
      },
      {
        type: "note",
        text: "Revenue and the funnel pipeline use the currency set in Settings → General.",
      },
    ],
  },
  {
    id: "leads",
    title: "Leads & Sales Pipeline",
    audience: ["admin", "teacher"],
    content: [
      {
        type: "paragraph",
        text: "Go to Leads in the sidebar to work the inbound pipeline: website contact messages and students moving through the funnel.",
      },
      {
        type: "subheading",
        text: "Contact messages",
      },
      {
        type: "paragraph",
        text: "Messages from the public contact form appear here. Mark them read, set a status (New, Contacted, Qualified, Converted, Archived), and add internal notes.",
      },
      {
        type: "subheading",
        text: "Convert a lead into a student",
      },
      {
        type: "paragraph",
        text: "Use Convert to create a student account from a contact. You can auto-approve and set a temporary password. If the email already has an account, it is linked instead of duplicated.",
      },
      {
        type: "subheading",
        text: "Funnel stages",
      },
      {
        type: "table",
        headers: ["Stage", "Meaning"],
        rows: [
          ["registered", "Account created, no action yet"],
          ["level_tested", "Completed the level test"],
          ["consultant_requested", "Asked to speak with a consultant (hot lead)"],
          ["awaiting_payment", "Started Stripe checkout"],
          ["approved", "Approved with an active plan"],
          ["onboarding_done / active", "Finished onboarding and learning"],
          ["payment_expired", "Plan expired or out of credits"],
          ["suspended", "Access revoked"],
        ],
      },
    ],
  },
  {
    id: "managing-students",
    title: "Managing Students",
    audience: ["admin", "teacher"],
    content: [
      {
        type: "paragraph",
        text: "Go to Students to view and manage every student account. New registrations start as Pending.",
      },
      {
        type: "list",
        items: [
          "Approve — activate access (also advances the funnel). Use Approve All Pending to bulk-approve.",
          "Suspend — revoke access; the student cannot log in until re-approved.",
          "Edit — update name, email, or phone.",
          "Reset Password — set a new password (admin only).",
          "Internal Notes — private notes not visible to the student.",
          "Trial booked — flag a student as having booked their trial.",
          "Enroll / Plan — assign a plan and credits directly (admin only).",
          "History — full financial and class history for the student (admin only).",
          "Delete — permanently removes the student; financial records are preserved as cancelled/void for audit.",
        ],
      },
      {
        type: "note",
        text: "Approving a student here only changes status. To grant lesson credits, create an enrollment in Billing or via the Plan action.",
      },
    ],
  },
  {
    id: "managing-teachers",
    title: "Managing Teachers (Team)",
    audience: ["admin"],
    content: [
      {
        type: "paragraph",
        text: "Go to Team (admin only) to manage staff accounts and their scheduling profiles.",
      },
      {
        type: "list",
        items: [
          "Add Teacher — name, email, password. Teachers share the admin panel but cannot access strict-admin areas (Team, Plans, Billing, Coupons, Availability, Media, CMS, Level Test, Testimonials, Settings).",
          "Edit / Reset Password / Delete — manage existing staff.",
          "Profile — set the teacher's timezone, booking buffer, and weekly availability template used by scheduling.",
        ],
      },
    ],
  },
  {
    id: "groups",
    title: "Groups",
    audience: ["admin", "teacher"],
    content: [
      {
        type: "paragraph",
        text: "Groups are named cohorts of students that can be assigned to classes together.",
      },
      {
        type: "list",
        items: [
          "Create a group — name, level (A1–C2), course type, schedule info, max students",
          "Add students — only approved students can join",
          "Assign to a class — choose Group as the class type; members are auto-enrolled, including in future group sessions",
        ],
      },
    ],
  },
  {
    id: "classes-scheduling",
    title: "Classes & Scheduling",
    audience: ["admin", "teacher"],
    content: [
      {
        type: "paragraph",
        text: "Go to Classes to create and manage sessions. Teachers see and manage only their own classes.",
      },
      {
        type: "subheading",
        text: "Creating a class",
      },
      {
        type: "list",
        items: [
          "Title, description, and type: Individual, Group, or Open",
          "Enrollment type: Open (students self-enroll) or Closed (you assign)",
          "Date, time, and duration",
          "Meeting link (Google Meet or Zoom)",
          "For Group classes, pick the group (members auto-enroll)",
        ],
      },
      {
        type: "note",
        text: "The system blocks double-booking: a class that overlaps the teacher's existing schedule (plus their booking buffer) is rejected with a conflict warning.",
      },
      {
        type: "subheading",
        text: "Completing a class & credits",
      },
      {
        type: "paragraph",
        text: "Marking a class Completed deducts one lesson credit from each enrolled student's active plan (once — it is idempotent). Use No-show to record an absence: a student no-show can deduct a credit, a teacher no-show refunds the credit if one was charged (per Settings → Scheduling policy).",
      },
      {
        type: "subheading",
        text: "Class statuses",
      },
      {
        type: "table",
        headers: ["Status", "Meaning"],
        rows: [
          ["Scheduled", "Upcoming, not yet held"],
          ["Full", "Maximum capacity reached"],
          ["Completed", "Held — credits deducted"],
          ["Cancelled", "Cancelled or no-show"],
        ],
      },
    ],
  },
  {
    id: "activities-grading",
    title: "Activities & Grading",
    audience: ["admin", "teacher"],
    content: [
      {
        type: "paragraph",
        text: "Activities are assignments you send to individual students. Go to Activities.",
      },
      {
        type: "subheading",
        text: "Creating an activity",
      },
      {
        type: "list",
        items: [
          "Title and description (the instructions)",
          "The student it is for",
          "Optional due date",
          "Optional Attachment URL — a resource link (video, document, website) visible to the student",
        ],
      },
      {
        type: "subheading",
        text: "Reviewing a submission",
      },
      {
        type: "list",
        items: [
          "Open the submitted activity",
          "Choose Reviewed — enter a grade from 0 to 100 plus optional feedback",
          "Or choose Needs Revision — leave feedback and send it back (no grade yet); the student can resubmit",
        ],
      },
      {
        type: "table",
        headers: ["Tab", "Shows"],
        rows: [
          ["All", "Every activity"],
          ["Pending", "Not yet submitted"],
          ["Submitted", "Submitted, awaiting review"],
          ["Needs Revision", "Sent back to the student"],
          ["Reviewed", "Reviewed with a grade"],
        ],
      },
    ],
  },
  {
    id: "plans",
    title: "Plans",
    audience: ["admin"],
    content: [
      {
        type: "paragraph",
        text: "Go to Plans (admin only) to define the packages students can buy. A plan controls how many lessons a student gets and for how long.",
      },
      {
        type: "table",
        headers: ["Field", "Description"],
        rows: [
          ["Name / Slug", "Display name and URL-safe identifier"],
          ["Price + Currency", "Amount and currency (defaults to the currency in Settings → General)"],
          ["Credits", "Number of lessons included"],
          ["Validity (days)", "How long the plan stays active after activation"],
          ["Type", "Individual, Group, or Both"],
          ["Max students", "Group cap (individual plans leave this empty)"],
          ["Recommended levels", "Level-test results this plan suits (e.g. A1-A2, B1) — used to suggest a plan at checkout"],
        ],
      },
      {
        type: "note",
        text: "Deleting a plan deactivates it (soft delete) so existing enrollments and reports stay intact.",
      },
    ],
  },
  {
    id: "billing",
    title: "Billing — Payments & Enrollments",
    audience: ["admin"],
    content: [
      {
        type: "paragraph",
        text: "Go to Billing (admin only) to register offline payments, manage enrollments, and adjust credits.",
      },
      {
        type: "subheading",
        text: "Register a payment",
      },
      {
        type: "list",
        items: [
          "Pick the student and (optionally) a plan — selecting a plan pre-fills the amount and currency",
          "Choose the method: Manual, PIX, Transfer, Cash, Courtesy",
          "Optionally apply a coupon code (validated live before saving)",
          "Leave 'create enrollment' on to grant credits and approve the student in one step",
        ],
      },
      {
        type: "subheading",
        text: "Enrollments & credits",
      },
      {
        type: "list",
        items: [
          "Each enrollment holds total credits, credits used, status, and an expiry date",
          "Add/remove credits manually for courtesy or corrections",
          "Cancel an enrollment to stop a student booking new classes",
          "A student is blocked from booking more upcoming classes than they have credits left",
        ],
      },
      {
        type: "note",
        text: "Stripe payments create payments and enrollments automatically via webhook. Manual entries here are for offline payments (cash, transfer, PIX, courtesy).",
      },
    ],
  },
  {
    id: "coupons",
    title: "Coupons",
    audience: ["admin"],
    content: [
      {
        type: "paragraph",
        text: "Go to Coupons (admin only) to create discount codes used at checkout and on manual payments.",
      },
      {
        type: "list",
        items: [
          "Type: Percent (e.g. 20% off) or Fixed (a flat amount off, in the configured currency)",
          "Max uses — optional total redemption limit",
          "Validity window — optional start/end dates",
          "Plan restriction — limit the coupon to specific plans (empty = any plan)",
        ],
      },
      {
        type: "note",
        text: "Codes are case-insensitive and stored uppercase. Deleting a coupon deactivates it (soft delete).",
      },
    ],
  },
  {
    id: "messages",
    title: "Messages",
    audience: ["admin", "teacher"],
    content: [
      {
        type: "paragraph",
        text: "Messages handles announcements and notifications with students (new activities, grades, etc.). Inbound website enquiries live under Leads.",
      },
    ],
  },
  {
    id: "chat",
    title: "Chat",
    audience: ["admin", "teacher"],
    content: [
      {
        type: "paragraph",
        text: "Go to Chat for direct 1-to-1 messaging with students.",
      },
      {
        type: "list",
        items: [
          "Pick a student from the list on the left",
          "Type and press Enter or click Send",
          "Unread conversations show a badge",
        ],
      },
      {
        type: "note",
        text: "Chat updates when you open or send a message (there is no live push), so refresh the conversation to pull new replies.",
      },
    ],
  },
  {
    id: "availability",
    title: "Availability Calendar",
    audience: ["admin"],
    content: [
      {
        type: "paragraph",
        text: "Go to Availability (admin only) to publish the bookable time slots students see when scheduling a 1:1 class.",
      },
      {
        type: "list",
        items: [
          "Pick a teacher (admins can manage any teacher; the slots belong to that teacher)",
          "Click a date to add time slots (start/end)",
          "Mark a date Blocked (holiday/unavailable) or add notes",
          "Set the teacher's timezone and booking buffer in their Team profile so slot times convert correctly",
        ],
      },
      {
        type: "note",
        text: "Students only ever see free slots — booked times appear as 'taken' with no personal details.",
      },
    ],
  },
  {
    id: "media",
    title: "Media Library",
    audience: ["admin"],
    content: [
      {
        type: "paragraph",
        text: "Go to Media (admin only) to upload and manage shared images and videos used across the site and CMS.",
      },
      {
        type: "list",
        items: [
          "Upload — images and videos (stored in Cloudinary when configured, otherwise local disk)",
          "Copy URL — reuse a file in the CMS, activities, or messages",
          "Delete — permanently removes the file",
        ],
      },
    ],
  },
  {
    id: "cms",
    title: "CMS — Page Content Editor",
    audience: ["admin"],
    content: [
      {
        type: "paragraph",
        text: "Go to Content (admin only) to edit public page copy and images without touching code.",
      },
      {
        type: "list",
        items: [
          "Pick a page (Home, About, Courses, Pricing, Book a Trial, etc.)",
          "Pick a section (Hero, Features, …)",
          "Edit text, titles, button labels, and images",
          "Save — changes go live on the public site immediately",
        ],
      },
    ],
  },
  {
    id: "testimonials-admin",
    title: "Testimonials",
    audience: ["admin"],
    content: [
      {
        type: "paragraph",
        text: "Go to Testimonials (admin only) to manage the reviews shown on the public site.",
      },
      {
        type: "list",
        items: [
          "Create/edit — name, location, short quote, excerpt, full story, photo, and SEO fields",
          "Status — Draft, Published, or Archived (only Published appear publicly)",
          "Featured + display order — control which show on the homepage and their order",
        ],
      },
    ],
  },
  {
    id: "level-test-admin",
    title: "Level Test Questions",
    audience: ["admin"],
    content: [
      {
        type: "paragraph",
        text: "Go to Level Test (admin only) to manage the questions in the public English assessment.",
      },
      {
        type: "list",
        items: [
          "Each question has a category, the prompt, exactly 4 options, and the correct answer",
          "Reorder questions and toggle them active/inactive",
          "New environments are seeded with 10 starter questions you can fully edit",
        ],
      },
    ],
  },
  {
    id: "settings",
    title: "Settings",
    audience: ["admin"],
    content: [
      {
        type: "paragraph",
        text: "Go to Settings (admin only) to configure the platform. Every integration is optional — the app runs without it and activates once you fill it in.",
      },
      {
        type: "subheading",
        text: "General",
      },
      {
        type: "list",
        items: [
          "Site name and tagline (header + SEO)",
          "Contact email (footer + notifications)",
          "Currency — drives all price, billing, and report display (EUR/BRL/USD/GBP)",
          "Trial booking URL and WhatsApp URL",
          "Social links — Facebook, Instagram, LinkedIn, YouTube",
        ],
      },
      {
        type: "subheading",
        text: "Integrations",
      },
      {
        type: "table",
        headers: ["Integration", "Notes"],
        rows: [
          ["Google Analytics / GTM", "Enable and paste the IDs to inject tracking on the public site"],
          ["reCAPTCHA (v3)", "Enable with a real v3 site + secret key; tune the score threshold (default 0.5)"],
          ["Stripe", "Publishable + Secret keys + Webhook secret; point the webhook at /api/webhooks/stripe"],
        ],
      },
      {
        type: "subheading",
        text: "Scheduling policy",
      },
      {
        type: "list",
        items: [
          "Cancellation window (hours) — how close to a class students can still cancel",
          "Deduct credit on student no-show",
          "Refund credit on teacher no-show",
          "Class reminder lead time (hours before)",
        ],
      },
      {
        type: "note",
        text: "Use the Change Password panel here to update your own admin password after the first login.",
      },
    ],
  },
  {
    id: "smtp",
    title: "Email (SMTP) Configuration",
    audience: ["admin"],
    content: [
      {
        type: "paragraph",
        text: "In Settings → Email, configure outbound transactional email. When disabled, emails are simply skipped — nothing breaks.",
      },
      {
        type: "table",
        headers: ["Field", "Description"],
        rows: [
          ["Enabled", "Toggle email sending on/off"],
          ["Sender Name / Email", "From name and address"],
          ["Reply-To", "Optional reply address"],
          ["SMTP Host / Port", "e.g. smtp.gmail.com · 587 (TLS) or 465 (SSL)"],
          ["Encryption", "None / SSL / TLS"],
          ["Username / Password", "SMTP credentials (password stored securely, never displayed)"],
        ],
      },
      {
        type: "note",
        text: "Click Send Test Email to verify the configuration before relying on it.",
      },
    ],
  },

  // ─── STUDENT ────────────────────────────────────────────────────────────────
  {
    id: "student-registration",
    title: "Getting Started",
    audience: ["student"],
    content: [
      {
        type: "list",
        items: [
          "Go to /register and enter your name, email, phone (optional), and password",
          "Your account starts as Pending and lands on a Getting Started screen",
          "From there you can take the free level test, choose a plan and pay (if online payments are on), or ask to talk to a consultant",
          "Once a plan is active (after payment or admin approval) you get full portal access",
        ],
      },
      {
        type: "note",
        text: "You can use the support chat at any time while your account is pending.",
      },
    ],
  },
  {
    id: "student-onboarding",
    title: "Onboarding Wizard",
    audience: ["student"],
    content: [
      {
        type: "paragraph",
        text: "After your plan is active, a short one-time wizard helps you get set up:",
      },
      {
        type: "list",
        items: [
          "Welcome — a quick intro",
          "Profile — confirm your name and contact details",
          "Learning goals — tell us what you want to achieve",
          "Schedule (optional) — book your first 1:1 class right away",
        ],
      },
      {
        type: "paragraph",
        text: "You only do this once; afterwards you go straight to the dashboard.",
      },
    ],
  },
  {
    id: "student-dashboard",
    title: "Student Dashboard",
    audience: ["student"],
    content: [
      {
        type: "paragraph",
        text: "Your dashboard highlights the single most important next step and your progress at a glance:",
      },
      {
        type: "list",
        items: [
          "Next step — e.g. choose a plan, renew, or join your imminent class",
          "Lessons left — credits remaining on your active plan",
          "Upcoming classes — with join links",
          "Activities due and unread messages",
        ],
      },
    ],
  },
  {
    id: "student-billing",
    title: "My Plan & Billing",
    audience: ["student"],
    content: [
      {
        type: "paragraph",
        text: "Go to Billing to see your active plan, lesson credits, and payment history.",
      },
      {
        type: "list",
        items: [
          "Active plan — credits used vs. total, and the expiry date",
          "Renew or upgrade — pick a plan and pay online (if Stripe is enabled) or contact the school",
          "History — past plans and completed payments",
        ],
      },
      {
        type: "note",
        text: "A lesson credit is used when a class is marked completed — not when you book it. You'll get low-credit reminders by email when your plan runs low.",
      },
    ],
  },
  {
    id: "student-schedule",
    title: "Schedule a 1:1 Class",
    audience: ["student"],
    content: [
      {
        type: "paragraph",
        text: "Go to Schedule to book a private class in one of your teacher's open slots.",
      },
      {
        type: "list",
        items: [
          "Pick a teacher, then a date with available slots (marked with a dot)",
          "Choose a free time slot and confirm",
          "The class appears in My Classes; your teacher adds the meeting link",
        ],
      },
      {
        type: "note",
        text: "You need an active plan with remaining credits to book, and you can't book more upcoming classes than you have credits.",
      },
    ],
  },
  {
    id: "student-classes",
    title: "My Classes",
    audience: ["student"],
    content: [
      {
        type: "paragraph",
        text: "Go to Classes to see everything you're enrolled in. Each card shows the date, time, teacher, duration, and a join link (Google Meet or Zoom) once set.",
      },
    ],
  },
  {
    id: "student-available",
    title: "Open Classes",
    audience: ["student"],
    content: [
      {
        type: "paragraph",
        text: "Go to Available to join open group classes anyone can enroll in.",
      },
      {
        type: "list",
        items: [
          "Enroll to join, Unenroll to leave (within the cancellation window)",
          "Classes marked Full have reached capacity",
          "You need an active plan with credits to enroll",
        ],
      },
    ],
  },
  {
    id: "student-activities",
    title: "Activities",
    audience: ["student"],
    content: [
      {
        type: "table",
        headers: ["Tab", "Meaning"],
        rows: [
          ["All", "Every activity"],
          ["Pending", "Not yet submitted"],
          ["Submitted", "Awaiting teacher review"],
          ["Needs Revision", "Your teacher asked you to redo it"],
          ["Reviewed", "Completed with a grade (0–100)"],
        ],
      },
      {
        type: "subheading",
        text: "Submitting an activity",
      },
      {
        type: "list",
        items: [
          "Open an activity and read the instructions and any teacher resource link",
          "Type your answer in the response box",
          "Optionally attach files — images, PDF, or documents (max 20 MB each)",
          "Click Submit Activity",
        ],
      },
      {
        type: "paragraph",
        text: 'If it comes back as "Revision Required", read the orange feedback, update your response, and resubmit.',
      },
    ],
  },
  {
    id: "student-messages",
    title: "Messages",
    audience: ["student"],
    content: [
      {
        type: "paragraph",
        text: "Go to Messages to read announcements and notifications from your teacher (new activities, grades). Opening a message marks it read.",
      },
    ],
  },
  {
    id: "student-chat",
    title: "Chat",
    audience: ["student"],
    content: [
      {
        type: "paragraph",
        text: "Go to Chat for direct messaging with your teacher (or support while pending). Type at the bottom and press Enter or Send; an unread badge shows new replies.",
      },
    ],
  },
  {
    id: "student-profile",
    title: "Profile",
    audience: ["student"],
    content: [
      {
        type: "paragraph",
        text: "Go to Profile to update your name and phone number, or change your password.",
      },
    ],
  },

  // ─── PARENT / VISITOR ───────────────────────────────────────────────────────
  {
    id: "public-website",
    title: "Public Website",
    audience: ["parent"],
    content: [
      {
        type: "paragraph",
        text: "The public website is open to everyone:",
      },
      {
        type: "table",
        headers: ["Page", "URL", "Description"],
        rows: [
          ["Home", "/", "Introduction, features, CTAs"],
          ["Courses", "/courses", "Course types and levels"],
          ["Pricing", "/pricing", "Plan options and fees"],
          ["About", "/about", "About the school and teachers"],
          ["Testimonials", "/testimonials", "Student reviews"],
          ["FAQ", "/faq", "Common questions"],
          ["Contact", "/contact", "Contact form"],
          ["Book a Trial", "/book-trial", "Request a trial class"],
          ["Level Test", "/level-test", "Free English level assessment"],
        ],
      },
    ],
  },
  {
    id: "level-test",
    title: "Level Test",
    audience: ["parent", "student"],
    content: [
      {
        type: "paragraph",
        text: "The level test at /level-test is a free, interactive quiz that estimates your English level. Answer the multiple-choice questions and click See My Result for your level (A1–C2) and a recommended plan. No account required.",
      },
    ],
  },
  {
    id: "contact-form",
    title: "Contact Form",
    audience: ["parent"],
    content: [
      {
        type: "paragraph",
        text: "At /contact, send the school a message with your name, email, inquiry type, and message. The school typically replies within 1–2 business days.",
      },
    ],
  },
  {
    id: "book-trial",
    title: "Book a Trial Class",
    audience: ["parent"],
    content: [
      {
        type: "paragraph",
        text: "At /book-trial, request a free introductory class. If a booking calendar is configured you'll go straight to it; otherwise the school contacts you to arrange a time.",
      },
    ],
  },

  // ─── FAQ ────────────────────────────────────────────────────────────────────
  {
    id: "faq",
    title: "Frequently Asked Questions",
    audience: ["admin", "teacher", "student", "parent"],
    content: [
      {
        type: "faq",
        items: [
          {
            q: "I registered but can't access everything yet.",
            a: "New accounts are Pending. From the Getting Started screen you can take the level test, choose a plan, or talk to a consultant. Full access opens once a plan is active (after payment or admin approval).",
          },
          {
            q: "How do lesson credits work?",
            a: "Each plan includes a number of lesson credits. A credit is used when a class is marked completed — not when you book it. You can't book more upcoming classes than you have credits remaining.",
          },
          {
            q: "How do I book a 1:1 class?",
            a: "Go to Schedule, pick a teacher and a date with open slots, choose a time, and confirm. You need an active plan with credits.",
          },
          {
            q: "How do I pay or renew?",
            a: "Go to Billing → Renew. If online payments are enabled you'll pay by card via Stripe; otherwise contact the school. Admins can also register offline payments (cash, transfer, PIX).",
          },
          {
            q: "Which currency are prices in?",
            a: "Whatever the school sets in Settings → General (e.g. EUR). Each plan and payment also stores its own currency.",
          },
          {
            q: "I forgot my password.",
            a: "Contact your teacher or the school administrator — they can reset it from the admin panel.",
          },
          {
            q: "My activity needs revision. What do I do?",
            a: "Open Activities → Needs Revision, read the orange feedback, update your response, and click Resubmit Activity. Grades are on a 0–100 scale.",
          },
          {
            q: "Can I upload files with a submission?",
            a: "Yes — images (JPG, PNG, GIF, WebP), PDF, Word (DOC/DOCX), and TXT, up to 20 MB each.",
          },
          {
            q: "Who do I contact for problems?",
            a: "Use Chat or Messages in the student portal. For account issues use the contact form at /contact.",
          },
        ],
      },
    ],
  },
];

export const getSectionsByAudience = (audience) =>
  DOC_SECTIONS.filter((s) => s.audience.includes(audience));
